/**
 * Font Manifest - Central registry of all available fonts
 * 
 * Helper functions for working with the font manifest.
 */

import { FONT_MANIFEST_DATA, type FontManifestEntry } from './FontManifestData';

// Re-export for convenience
export type { FontManifestEntry };
export const FONT_MANIFEST = FONT_MANIFEST_DATA;

/**
 * Helper: Get font by display name
 */
export function getFontByDisplayName(displayName: string): FontManifestEntry | undefined {
  return FONT_MANIFEST.find(f => f.displayName === displayName);
}

/**
 * Helper: Get font by family name
 */
export function getFontByFamily(family: string): FontManifestEntry | undefined {
  return FONT_MANIFEST.find(f => f.family === family);
}

/**
 * Helper: Get font by UID
 */
export function getFontByUid(font_uid: string): FontManifestEntry | undefined {
  return FONT_MANIFEST.find(f => f.font_uid === font_uid);
}

/**
 * Helper: Get all court-compliant fonts
 */
export function getCourtCompliantFonts(): FontManifestEntry[] {
  return FONT_MANIFEST.filter(f => f.court_compliant);
}

/**
 * Helper: Get fonts by category
 */
export function getFontsByCategory(category: FontManifestEntry['category']): FontManifestEntry[] {
  return FONT_MANIFEST.filter(f => f.category === category);
}

/**
 * Helper: Map display name to backend font_uid
 * Example: "Times New Roman" → "NimbusRoman-Regular.otf"
 */
export function mapDisplayNameToFontUid(displayName: string): string | undefined {
  const font = getFontByDisplayName(displayName);
  return font?.font_uid;
}

/**
 * Helper: Map family to backend font_uid
 * Example: "EdgeDocs Roman" → "NimbusRoman-Regular.otf"
 */
export function mapFamilyToFontUid(family: string): string | undefined {
  const font = getFontByFamily(family);
  return font?.font_uid;
}

/**
 * Helper: Map family to display name for UI
 * Example: "EdgeDocs Roman" → "Times New Roman"
 */
export function mapFamilyToDisplayName(family: string): string {
  const font = getFontByFamily(family);
  return font?.displayName || family;
}

/**
 * Helper: Map display name to family for CSS
 * Example: "Times New Roman" → "EdgeDocs Roman"
 */
export function mapDisplayNameToFamily(displayName: string): string | undefined {
  const font = getFontByDisplayName(displayName);
  return font?.family;
}

/**
 * 🆕 2026-01-21: Resolve font_uid from family, weight, and style
 *
 * This is the key function for MS Word-style formatting - it maps
 * formatting attributes (like fontWeight: 700 for bold) to the correct
 * font file (like Inter-Bold.ttf).
 *
 * @param family - Font family name (e.g., "Inter")
 * @param weight - Font weight (100-900, default 400)
 * @param style - Font style ('normal' | 'italic' | 'oblique', default 'normal')
 * @returns font_uid if found, undefined if no exact or close match
 */
export function resolveFontUid(
  family: string,
  weight: number = 400,
  style: 'normal' | 'italic' | 'oblique' = 'normal'
): string | undefined {
  // First, try exact match
  const exactMatch = FONT_MANIFEST.find(
    f => f.family === family && f.weight === weight && f.style === style
  );
  if (exactMatch) {
    return exactMatch.font_uid;
  }

  // If no exact match, find closest weight in same family and style
  const sameStyleFonts = FONT_MANIFEST.filter(
    f => f.family === family && f.style === style
  );

  if (sameStyleFonts.length > 0) {
    // Find closest weight
    let closestFont = sameStyleFonts[0];
    let closestDiff = Math.abs(closestFont.weight - weight);

    for (const font of sameStyleFonts) {
      const diff = Math.abs(font.weight - weight);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestFont = font;
      }
    }

    return closestFont.font_uid;
  }

  // Fallback: find any font with same family (ignore style)
  const sameFamilyFont = FONT_MANIFEST.find(f => f.family === family);
  return sameFamilyFont?.font_uid;
}

/**
 * 🆕 2026-01-21: Get all font UIDs needed for a font family
 *
 * Returns all variants (regular, bold, italic, bold-italic) of a font family.
 * Used to pre-load all variants when a document uses a specific font family.
 *
 * @param family - Font family name (e.g., "Inter", "Liberation Serif")
 * @returns Array of font_uid strings for all variants of the family
 */
export function getFontFamilyVariants(family: string): string[] {
  return FONT_MANIFEST
    .filter(f => f.family === family)
    .map(f => f.font_uid);
}
